import SchoolIcon from "@mui/icons-material/School";
import {
  CheckCircle,
  Download,
  FileText,
  MapPin,
  Play,
  User,
  Volume2,
  X,
} from "lucide-react";
import { useContext, useEffect, useState } from "react";
import CoursesApi from "../../api/coursesApi";
import DialogChatAPI from "../../api/dialogChatApi";
import Loader from "../../components/Loader/Loader";
import ModalWindow from "../../components/ModalWindow/ModalWindow";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { AvailableLangs, ISO639Lang } from "../../constans/languagesList";
import { ContextProvider } from "../../contextProvider";
import { Course, LectureFiles, TypeCoursesFilters } from "../../types/Courses";
import { getTranslation } from "../../utils/getTranslation";
import Notify, { SuccesNotify } from "../../utils/toaster";
import { ReviewMessageModal } from "../ReviewSpecialists/components/ReviewMessageModal";
import WebinarTeam from "../Webinars/WebinarTeam/WebinarTeam";
import s from "../Webinars/Webinars.module.css";

interface ReviewStats {
  totalPending: number;
  reviewedToday: number;
  averageReviewTime: number;
  queuePosition: number;
}

interface CourseFile {
  name: string;
  url: string;
  type: "image" | "video" | "audio" | "certificate" | "document";
}

interface FilePreviewState {
  isOpen: boolean;
  file: CourseFile | null;
}

const Courses = () => {
  const token = localStorage.getItem("token");
  const { userData } = useContext(ContextProvider);

  const [reviewQueue, setReviewQueue] = useState<Course[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoader, setIsLoader] = useState(false);
  const [avatars, setAvatars] = useState<{ [courseId: string]: string }>({});
  const [courseFiles, setCourseFiles] = useState<CourseFile[]>([]);
  const [courseStructureFiles, setCourseStructureFiles] = useState<
    LectureFiles[]
  >([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);

  const [stats, setStats] = useState<ReviewStats>({
    totalPending: 0,
    reviewedToday: 0,
    averageReviewTime: 0,
    queuePosition: 0,
  });
  const [reviewTimes, setReviewTimes] = useState<number[]>([]);
  const [currentReviewStart, setCurrentReviewStart] = useState<number>(
    Date.now(),
  );

  const [isApprovalMessageModalOpen, setIsApprovalMessageModalOpen] =
    useState(false);
  const [isRejectionMessageModalOpen, setIsRejectionMessageModalOpen] =
    useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<
    "approve" | "reject"
  >("approve");
  const [filePreview, setFilePreview] = useState<FilePreviewState>({
    isOpen: false,
    file: null,
  });

  const currentCourse = reviewQueue[currentIndex];

  useEffect(() => {
    loadReviewQueue();
  }, []);

  useEffect(() => {
    const loadAvatars = async () => {
      if (token && reviewQueue.length > 0) {
        const coursesToLoad = reviewQueue.slice(
          currentIndex,
          Math.min(currentIndex + 5, reviewQueue.length),
        );

        const promises = coursesToLoad.map((course) =>
          CoursesApi.getAllCourseFileSignedUrls(token, course._id),
        );
        const responses = await Promise.all(promises);
        const avatarsData: { [id: string]: string } = {};

        responses.forEach((response, idx) => {
          if (response.urls?.coverImage) {
            avatarsData[coursesToLoad[idx]._id] = response.urls.coverImage;
          }
        });

        setAvatars((prev) => ({ ...prev, ...avatarsData }));
      }
    };
    loadAvatars();
  }, [reviewQueue, currentIndex, token]);

  useEffect(() => {
    setStats((prev) => ({
      ...prev,
      totalPending: reviewQueue.length - currentIndex,
      queuePosition: currentIndex + 1,
    }));
  }, [reviewQueue, currentIndex]);

  useEffect(() => {
    setCurrentReviewStart(Date.now());
  }, [currentIndex]);

  useEffect(() => {
    const loadCourseFiles = async () => {
      if (!token || !currentCourse) {
        setCourseFiles([]);
        setCourseStructureFiles([]);
        return;
      }

      setIsLoadingFiles(true);
      try {
        const response = await CoursesApi.getAllCourseFileSignedUrls(
          token,
          currentCourse._id,
        );

        if (response.status && response.urls) {
          const files: CourseFile[] = [];

          if (response.urls.coverImage) {
            files.push({
              name: "Course Cover",
              url: response.urls.coverImage,
              type: "image",
            });
          }
          if (response.urls.promoVideo) {
            files.push({
              name: "Promo Video",
              url: response.urls.promoVideo,
              type: "video",
            });
          }
          if (response.urls.certificate) {
            files.push({
              name: "Certificate Template",
              url: response.urls.certificate,
              type: "certificate",
            });
          }

          setCourseFiles(files);
          setCourseStructureFiles(response.urls.chapters || []);
        } else {
          setCourseFiles([]);
          setCourseStructureFiles([]);
        }
      } catch (error) {
        console.error("Error loading course files:", error);
        setCourseFiles([]);
        setCourseStructureFiles([]);
      } finally {
        setIsLoadingFiles(false);
      }
    };

    loadCourseFiles();
  }, [token, currentCourse]);

  const loadReviewQueue = async () => {
    if (!token) return;

    setIsLoader(true);
    try {
      const filters: TypeCoursesFilters = {
        page: 1,
        limit: 100,
        isApproveRequested: true,
      };

      const response = await CoursesApi.getFilteredCourses(token, filters);

      if (response.status && response.courses) {
        setReviewQueue(response.courses);
        setStats((prev) => ({
          ...prev,
          totalPending: response.courses!.length,
        }));
      } else {
        setReviewQueue([]);
      }
    } catch (error) {
      console.error("Error loading review queue:", error);
      Notify("Failed to load review queue");
    } finally {
      setIsLoader(false);
    }
  };

  const recordReviewTime = () => {
    const reviewTime = Date.now() - currentReviewStart;
    const newReviewTimes = [...reviewTimes, reviewTime];
    setReviewTimes(newReviewTimes);

    const avgTime =
      newReviewTimes.reduce((a, b) => a + b, 0) / newReviewTimes.length;
    setStats((prev) => ({
      ...prev,
      averageReviewTime: Math.round(avgTime / 1000),
      reviewedToday: prev.reviewedToday + 1,
    }));
  };

  const handleApproveClick = () => {
    setConfirmationAction("approve");
    setIsConfirmationModalOpen(true);
  };

  const handleRejectClick = () => {
    setConfirmationAction("reject");
    setIsConfirmationModalOpen(true);
  };

  const handleApproveWithMessage = async (message: string) => {
    if (!token || !currentCourse || !userData) return;

    try {
      await sendAdminMessage(message);

      const response = await CoursesApi.update(token, {
        _id: currentCourse._id,
        isApproved: true,
        isApproveRequested: false,
      });

      if (response.status) {
        recordReviewTime();
        setIsApprovalMessageModalOpen(false);
        SuccesNotify("Course approved and specialist notified");

        setReviewQueue((prev) => prev.filter((_, idx) => idx !== currentIndex));
      } else if (response.message) {
        Notify(response.message);
      }
    } catch (error) {
      console.error("Error approving course:", error);
      Notify("Failed to approve course");
    }
  };

  const handleRejectWithMessage = async (message: string) => {
    if (!token || !currentCourse || !userData) return;

    try {
      await sendAdminMessage(message);

      const response = await CoursesApi.update(token, {
        _id: currentCourse._id,
        isApproved: false,
        isApproveRequested: false,
      });

      if (response.status) {
        recordReviewTime();
        setIsRejectionMessageModalOpen(false);
        SuccesNotify("Course rejected and specialist notified with feedback");

        setReviewQueue((prev) => prev.filter((_, idx) => idx !== currentIndex));
      } else if (response.message) {
        Notify(response.message);
      }
    } catch (error) {
      console.error("Error rejecting course:", error);
      Notify("Failed to reject course");
    }
  };

  const sendAdminMessage = async (message: string) => {
    if (!token || !userData || !currentCourse || !currentCourse.createdBy) {
      return;
    }

    const chatResponse = await DialogChatAPI.createDialogChat(token, {
      userId: userData._id,
      userIdToSend: currentCourse.createdBy._id,
    });

    if (!chatResponse.status) {
      throw new Error("Failed to create chat");
    }

    await DialogChatAPI.sendMessage({
      userIdToSend: currentCourse.createdBy._id,
      message: message,
      userId: userData._id,
      date: new Date(),
      files: [],
    });
  };

  const moveToNext = () => {
    if (currentIndex < reviewQueue.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      loadReviewQueue();
      setCurrentIndex(0);
    }
  };

  const moveToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "—";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Invalid date";
    }
  };

  const getSpecialistName = (): string => {
    if (!currentCourse?.createdBy) return "Specialist";
    const firstName = Array.isArray(currentCourse.createdBy.name)
      ? currentCourse.createdBy.name[0]?.text
      : currentCourse.createdBy.name || "";
    const lastName = Array.isArray(currentCourse.createdBy.surname)
      ? currentCourse.createdBy.surname[0]?.text
      : currentCourse.createdBy.surname || "";
    return `${firstName} ${lastName}`.trim();
  };

  const getTotalLectures = (course: Course) =>
    (course.chapters || []).reduce(
      (total, chapter) => total + (chapter.lectures?.length || 0),
      0,
    );

  const handleFileClick = (file: CourseFile) => {
    setFilePreview({ isOpen: true, file });
  };

  const closeFilePreview = () => {
    setFilePreview({ isOpen: false, file: null });
  };

  const getLectureFilesByChapter = (chapterId?: string) => {
    if (!chapterId) return undefined;
    return courseStructureFiles.find((chapter) => chapter._id === chapterId);
  };

  const getLectureFileBadges = (
    chapterId?: string,
    lectureId?: string,
  ): CourseFile[] => {
    if (!chapterId || !lectureId) return [];

    const chapter = getLectureFilesByChapter(chapterId);
    const lecture = chapter?.lectures?.find((item) => item._id === lectureId);

    if (!lecture) return [];

    const lectureFiles: CourseFile[] = [];

    if (lecture.video) {
      lectureFiles.push({
        name: "Lecture Video",
        url: lecture.video,
        type: "video",
      });
    }

    if (lecture.audio) {
      lectureFiles.push({
        name: "Lecture Audio",
        url: lecture.audio,
        type: "audio",
      });
    }

    if (lecture.article) {
      lectureFiles.push({
        name: "Lecture Article",
        url: lecture.article,
        type: "document",
      });
    }

    return lectureFiles;
  };

  const courseApprovalTemplates: {
    [key in AvailableLangs]?: (name: string, spec?: string) => string;
  } = {
    en: (name: string) => `Dear ${name},

Congratulations! Your course "${currentCourse.title}" has been reviewed and approved.

Activate your course to make it available to users on the platform.

Thank you for creating valuable content and being part of our platform!

Best regards,
Review Team`,

    ru: (name: string) => `Уважаемый(ая) ${name},

Поздравляем! Ваш курс «${currentCourse.title}» был проверен и одобрен.

Активируйте свой курс, чтобы он был доступен пользователям платформы.

Спасибо за ваш вклад и качественный контент!

С уважением,
Команда модерации`,

    uk: (name: string) => `Шановний(а) ${name},

Вітаємо! Ваш курс «${currentCourse.title}» було перевірено та схвалено.

Активуйте свій курс, щоб він був доступний користувачам платформи.

Дякуємо за ваш внесок і корисний контент!

З повагою,
Команда модерації`,
  };

  const courseRejectionTemplates: {
    [key in AvailableLangs]?: (
      name: string,
      spec?: string,
      fields?: string,
    ) => string;
  } = {
    en: (name: string, reasons?: string) => {
      let msg = `Dear ${name},

Thank you for submitting your course "${currentCourse.title}" for review.

Unfortunately, we cannot approve this course at the moment. Please review the feedback below and make the necessary changes before resubmitting:

`;
      if (reasons) {
        msg += reasons + "\n\n";
      } else {
        msg += "[Please specify the reasons for rejection]\n\n";
      }

      msg += `Once the issues are resolved, you may resubmit the course for review.

Best regards,
Review Team`;

      return msg;
    },

    ru: (name: string, reasons?: string) => {
      let msg = `Уважаемый(ая) ${name},

Спасибо за отправку курса «${currentCourse.title}» на модерацию.

К сожалению, мы не можем одобрить данный курс в текущем виде. Пожалуйста, ознакомьтесь с замечаниями ниже и внесите необходимые изменения перед повторной отправкой:

`;
      if (reasons) {
        msg += reasons + "\n\n";
      } else {
        msg += "[Пожалуйста, укажите причины отклонения]\n\n";
      }

      msg += `После внесения изменений вы сможете повторно отправить курс на модерацию.

С уважением,
Команда модерации`;

      return msg;
    },

    uk: (name: string, reasons?: string) => {
      let msg = `Шановний(а) ${name},

Дякуємо за подання курсу «${currentCourse.title}» на перевірку.

На жаль, ми не можемо схвалити цей курс у поточному вигляді. Будь ласка, ознайомтеся з зауваженнями нижче та внесіть необхідні зміни перед повторним поданням:

`;
      if (reasons) {
        msg += reasons + "\n\n";
      } else {
        msg += "[Будь ласка, вкажіть причини відхилення]\n\n";
      }

      msg += `Після внесення змін ви зможете повторно подати курс на перевірку.

З повагою,
Команда модерації`;

      return msg;
    },
  };

  if (isLoader) {
    return (
      <div className={s.loaderContainer}>
        <Loader size={80} />
        <p className={s.loadingText}>Loading course review queue...</p>
      </div>
    );
  }

  if (reviewQueue.length === 0) {
    return (
      <div className={s.emptyContainer}>
        <div className={s.emptyState}>
          <CheckCircle className={s.emptyIcon} size={64} />
          <h2 className={s.emptyTitle}>All courses reviewed! 🎉</h2>
          <p className={s.emptyText}>
            There are no courses waiting for review at the moment.
          </p>
          {stats.reviewedToday > 0 && (
            <div className={s.statsCard}>
              <div className={s.sessionStats}>
                <div className={s.statItem}>
                  <span className={s.statLabel}>Reviewed Today</span>
                  <span className={s.statValue}>{stats.reviewedToday}</span>
                </div>
                {stats.averageReviewTime > 0 && (
                  <div className={s.statItem}>
                    <span className={s.statLabel}>Avg Review Time</span>
                    <span className={s.statValue}>
                      {formatTime(stats.averageReviewTime)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
          <Button onClick={loadReviewQueue} className="mt-4">
            Refresh Queue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={s.mainLayout}>
      <div className={s.header}>
        <div className={s.headerContent}>
          <div className={s.titleBlock}>
            <h1 className={s.title}>Course Review</h1>
            <Badge variant="destructive" className={s.pendingBadge}>
              {stats.totalPending} Pending
            </Badge>
          </div>
          <Button variant="outline" onClick={loadReviewQueue} size="sm">
            Refresh
          </Button>
        </div>

        <div className={s.progressContainer}>
          <div className={s.progressBar}>
            <div
              className={s.progressFill}
              style={{
                width: `${(currentIndex / reviewQueue.length) * 100}%`,
              }}
            />
          </div>
          <p className={s.progressText}>
            {stats.queuePosition} / {reviewQueue.length}
          </p>
        </div>
      </div>

      <div className={s.contentArea}>
        <div className={s.listPanel}>
          <div className={s.listHeader}>
            <h2 className={s.listTitle}>Queue ({reviewQueue.length})</h2>
          </div>
          <div className={s.webinarList}>
            {reviewQueue.map((course, idx) => (
              <div
                key={course._id}
                className={`${s.listItem} ${idx === currentIndex ? s.active : ""}`}
                onClick={() => setCurrentIndex(idx)}
              >
                <div className={s.listItemNumber}>{idx + 1}</div>
                <div className={s.listItemContent}>
                  <div className={s.listItemTitle}>{course.title}</div>
                  <div className={s.listItemMeta}>
                    {ISO639Lang[course.language as AvailableLangs] ||
                      course.language}
                    {course.learningMode ? ` • ${course.learningMode}` : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className={s.listNavigation}>
            <Button
              variant="outline"
              size="sm"
              onClick={moveToPrevious}
              disabled={currentIndex === 0}
              className={s.listNavBtn}
            >
              ← Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={moveToNext}
              className={s.listNavBtn}
            >
              Skip →
            </Button>
          </div>
        </div>

        {currentCourse && (
          <div className={s.detailsPanel}>
            <div className={s.detailsHeader}>
              {avatars[currentCourse._id] ? (
                <img
                  src={avatars[currentCourse._id]}
                  alt={currentCourse.title}
                  className={s.detailsAvatar}
                />
              ) : (
                <div className={s.detailsAvatarPlaceholder}>
                  <MapPin size={32} />
                </div>
              )}
              <div className={s.detailsHeaderContent}>
                <h2 className={s.detailsTitle}>{currentCourse.title}</h2>
                <p className={s.detailsBy}>
                  <User size={14} />
                  {getSpecialistName()}
                </p>
                {currentCourse.educationalHub && (
                  <p className={s.detailsBy}>
                    <SchoolIcon style={{ width: "14px", height: "14px" }} />
                    {getTranslation(currentCourse.educationalHub.title, "en")}
                  </p>
                )}
              </div>
            </div>

            <div className={s.detailsScroll}>
              <div className={s.infoGrid}>
                <div className={s.infoItem}>
                  <span className={s.infoLabel}>Requested At</span>
                  <span className={s.infoValue}>
                    {formatDate(currentCourse.approveRequestedAt)}
                  </span>
                </div>
                <div className={s.infoItem}>
                  <span className={s.infoLabel}>Language</span>
                  <span className={s.infoValue}>
                    {ISO639Lang[currentCourse.language as AvailableLangs] ||
                      currentCourse.language}
                  </span>
                </div>
                <div className={s.infoItem}>
                  <span className={s.infoLabel}>Learning Mode</span>
                  <span className={s.infoValue}>
                    {currentCourse.learningMode || "—"}
                  </span>
                </div>
                <div className={s.infoItem}>
                  <span className={s.infoLabel}>Price</span>
                  <span className={s.infoValue}>
                    {currentCourse.cost / 100} USD
                  </span>
                </div>
                <div className={s.infoItem}>
                  <span className={s.infoLabel}>Chapters</span>
                  <span className={s.infoValue}>
                    {currentCourse.chapters?.length || 0}
                  </span>
                </div>
                <div className={s.infoItem}>
                  <span className={s.infoLabel}>Lectures</span>
                  <span className={s.infoValue}>
                    {getTotalLectures(currentCourse)}
                  </span>
                </div>
              </div>

              {currentCourse.briefDescription && (
                <div className={s.contentSection}>
                  <h3 className={s.sectionTitle}>Brief Description</h3>
                  <p className={s.sectionText}>
                    {currentCourse.briefDescription}
                  </p>
                </div>
              )}

              {currentCourse.description && (
                <div className={s.contentSection}>
                  <h3 className={s.sectionTitle}>Description</h3>
                  <p className={s.sectionText}>{currentCourse.description}</p>
                </div>
              )}

              {currentCourse.targetAudienceText &&
                currentCourse.targetAudienceText.length > 0 && (
                  <div className={s.contentSection}>
                    <h3 className={s.sectionTitle}>Target Audience</h3>
                    <ul className={s.bulletList}>
                      {currentCourse.targetAudienceText.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

              {currentCourse.participantBenefitsText &&
                currentCourse.participantBenefitsText.length > 0 && (
                  <div className={s.contentSection}>
                    <h3 className={s.sectionTitle}>Participant Benefits</h3>
                    <ul className={s.bulletList}>
                      {currentCourse.participantBenefitsText.map(
                        (item, idx) => (
                          <li key={idx}>{item}</li>
                        ),
                      )}
                    </ul>
                  </div>
                )}

              {currentCourse.chapters && currentCourse.chapters.length > 0 && (
                <div className={s.contentSection}>
                  <h3 className={s.sectionTitle}>Course Structure</h3>
                  <div className={s.agendaList}>
                    {courseStructureFiles.map((chapter) => (
                      <div key={chapter._id} className={s.agendaItem}>
                        <div className={s.agendaItemContent}>
                          <div className={s.agendaTitle}>{chapter.title}</div>
                          {chapter.description && (
                            <div className={s.agendaDuration}>
                              {chapter.description}
                            </div>
                          )}

                          {chapter.lectures && chapter.lectures.length > 0 && (
                            <div className={s.lectureList}>
                              {chapter.lectures.map((lecture, lectureIndex) => {
                                const lectureFiles = getLectureFileBadges(
                                  chapter._id,
                                  lecture._id,
                                );
                                const chapterFiles = getLectureFilesByChapter(
                                  chapter._id,
                                );
                                const lectureWithContent =
                                  chapterFiles?.lectures?.find(
                                    (item) => item._id === lecture._id,
                                  );

                                return (
                                  <div
                                    key={lecture._id || lectureIndex}
                                    className={s.lectureItem}
                                  >
                                    <div className={s.lectureHeader}>
                                      <div className={s.lectureTitle}>
                                        {lecture.order ?? lectureIndex + 1}.{" "}
                                        {lecture.title}
                                      </div>
                                    </div>

                                    {lecture.description && (
                                      <div className={s.lectureDescription}>
                                        {lecture.description}
                                      </div>
                                    )}

                                    {lectureWithContent?.text && (
                                      <div
                                        className={s.lectureTextPreview}
                                        dangerouslySetInnerHTML={{
                                          __html: lectureWithContent.text,
                                        }}
                                      ></div>
                                    )}

                                    {lectureFiles.length > 0 && (
                                      <div className={s.lectureFilesRow}>
                                        {lectureFiles.map((file, fileIdx) => (
                                          <button
                                            key={`${lecture._id}-${fileIdx}`}
                                            className={s.lectureFileButton}
                                            onClick={() =>
                                              handleFileClick(file)
                                            }
                                          >
                                            {file.type === "video" ? (
                                              <Play size={14} />
                                            ) : file.type === "audio" ? (
                                              <Volume2 size={14} />
                                            ) : (
                                              <FileText size={14} />
                                            )}
                                            {file.name}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <div className={s.agendaMeta}>
                          <span className={s.agendaBadge}>
                            {chapter.lectures?.length || 0} lectures
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className={s.contentSection}>
                <h3 className={s.sectionTitle}>Materials & Files</h3>
                {isLoadingFiles ? (
                  <div className={s.filesLoading}>
                    <Loader size={24} />
                    <p>Loading files...</p>
                  </div>
                ) : courseFiles.length > 0 ? (
                  <div className={s.filesGrid}>
                    {courseFiles.map((file, idx) => (
                      <div
                        key={idx}
                        className={s.fileCard}
                        onClick={() => handleFileClick(file)}
                      >
                        <div className={s.fileCardIcon}>
                          {file.type === "video" ? (
                            <Play size={24} />
                          ) : (
                            <FileText size={24} />
                          )}
                        </div>
                        <div className={s.fileCardContent}>
                          <div className={s.fileCardName}>{file.name}</div>
                          <div className={s.fileCardType}>{file.type}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={s.filesEmpty}>No materials uploaded yet</p>
                )}
              </div>

              <div className={s.contentSection}>
                <h3 className={s.sectionTitle}>Settings</h3>
                <div className={s.settingsList}>
                  <div className={s.settingItem}>
                    <span className={s.settingLabel}>Course active</span>
                    <span className={s.settingValue}>
                      {currentCourse.isActive ? "Yes" : "No"}
                    </span>
                  </div>
                  <div className={s.settingItem}>
                    <span className={s.settingLabel}>Issue certificate</span>
                    <span className={s.settingValue}>
                      {currentCourse.certificateConfig?.issue ? "Yes" : "No"}
                    </span>
                  </div>
                  <div className={s.settingItem}>
                    <span className={s.settingLabel}>Offers enabled</span>
                    <span className={s.settingValue}>
                      {currentCourse.offersSwitch ? "Yes" : "No"}
                    </span>
                  </div>
                </div>
              </div>

              {currentCourse.coSpeakerUser && (
                <div className={s.contentSection}>
                  <h3 className={s.sectionTitle}>Course team</h3>
                  <div>
                    <p>Co-speaker</p>
                    <WebinarTeam
                      searchType="coSpeakerUser"
                      userId={currentCourse.coSpeakerUser.shortId}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className={s.detailsActions}>
              <Button
                variant="destructive"
                onClick={handleRejectClick}
                className="flex-1"
              >
                Reject
              </Button>
              <Button
                onClick={handleApproveClick}
                className="flex-1"
                variant={"success"}
              >
                Approve
              </Button>
            </div>
          </div>
        )}
      </div>

      <ModalWindow
        isOpen={isConfirmationModalOpen}
        setIsOpen={setIsConfirmationModalOpen}
        title={
          confirmationAction === "approve"
            ? "Approve Course?"
            : "Reject Course?"
        }
        width="fit-content"
      >
        <div className={s.confirmationContent}>
          <p>
            You are about to{" "}
            <strong>
              {confirmationAction === "approve" ? "approve" : "reject"}
            </strong>{" "}
            this course. The specialist will be notified with a message.
          </p>
          <div className={s.confirmationActions}>
            <Button
              variant="outline"
              onClick={() => setIsConfirmationModalOpen(false)}
              className={s.cancelBtn}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setIsConfirmationModalOpen(false);
                if (confirmationAction === "approve") {
                  setIsApprovalMessageModalOpen(true);
                } else {
                  setIsRejectionMessageModalOpen(true);
                }
              }}
            >
              Continue
            </Button>
          </div>
        </div>
      </ModalWindow>

      <ModalWindow
        isOpen={isApprovalMessageModalOpen}
        setIsOpen={setIsApprovalMessageModalOpen}
        title="Send Approval Message"
        width="fit-content"
      >
        {currentCourse && (
          <ReviewMessageModal
            isApproval={true}
            specialistName={getSpecialistName()}
            onSend={handleApproveWithMessage}
            onCancel={() => setIsApprovalMessageModalOpen(false)}
            userSelectedLang={(currentCourse.language as any) || "en"}
            approvalTemplatesProps={courseApprovalTemplates}
          />
        )}
      </ModalWindow>

      <ModalWindow
        isOpen={isRejectionMessageModalOpen}
        setIsOpen={setIsRejectionMessageModalOpen}
        title="Send Rejection Message"
        width="fit-content"
      >
        {currentCourse && (
          <ReviewMessageModal
            isApproval={false}
            specialistName={getSpecialistName()}
            onSend={handleRejectWithMessage}
            onCancel={() => setIsRejectionMessageModalOpen(false)}
            userSelectedLang={(currentCourse.language as any) || "en"}
            rejectionTemplatesProps={courseRejectionTemplates}
          />
        )}
      </ModalWindow>

      {filePreview.file && (
        <div className={s.filePreviewOverlay} onClick={closeFilePreview}>
          <div
            className={s.filePreviewModal}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={s.filePreviewHeader}>
              <h3 className={s.filePreviewTitle}>{filePreview.file.name}</h3>
              <button
                className={s.filePreviewClose}
                onClick={closeFilePreview}
                aria-label="Close"
              >
                <X size={24} />
              </button>
            </div>

            <div className={s.filePreviewContent}>
              {filePreview.file.type === "image" ? (
                <img
                  src={filePreview.file.url}
                  alt={filePreview.file.name}
                  className={s.previewImage}
                />
              ) : filePreview.file.type === "video" ? (
                <video
                  controls
                  className={s.previewVideo}
                  src={filePreview.file.url}
                >
                  Your browser does not support the video tag.
                </video>
              ) : filePreview.file.type === "audio" ? (
                <audio
                  controls
                  src={filePreview.file.url}
                  className={s.previewAudio}
                >
                  Your browser does not support the audio element.
                </audio>
              ) : (
                <div className={s.filePreviewPlaceholder}>
                  <FileText size={64} />
                  <p>{filePreview.file.name}</p>
                  <a
                    href={filePreview.file.url}
                    download
                    className={s.downloadButton}
                  >
                    <Download size={16} />
                    Download
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Courses;
